import { privacyPath } from "../../constants.ts";
import { usePageMeta } from "./pageMeta.ts";
import { Support } from "./support.tsx";

const sectionStyle =
  "mb-6 text-text-secondary-light dark:text-text-secondary leading-relaxed text-sm";
const headingStyle =
  "text-lg font-semibold mt-8 mb-3 text-text-primary-light dark:text-text-primary";

export const TermsOfService = () => {
  usePageMeta({
    title: "Terms of Service | prompt2bot",
    description: "Terms that govern your use of the prompt2bot platform.",
  });
  return (
    <div className="max-w-2xl mx-auto my-10 p-6 rounded-lg shadow-md bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary">
      <h1 className="text-2xl font-bold mb-2 text-center">Terms of Service</h1>
      <p className="text-center text-xs text-text-tertiary-light dark:text-text-tertiary mb-8">
        Last updated: August 5, 2026
      </p>

      <p className={sectionStyle}>
        These Terms of Service ("Terms") govern your access to and use of the
        prompt2bot platform and services ("Service") operated by prompt2bot
        ("we", "us", "our"). By creating an account or using the Service, you
        agree to be bound by these Terms. If you do not agree, do not use the
        Service.
      </p>

      <h2 className={headingStyle}>1. Description of Service</h2>
      <p className={sectionStyle}>
        prompt2bot is a platform that allows users to create, deploy, and manage
        AI-powered agents across multiple channels including web, Telegram,
        WhatsApp, and more. The Service includes bot creation, hosting,
        conversation management, analytics, and related features.
      </p>

      <h2 className={headingStyle}>2. Eligibility</h2>
      <p className={sectionStyle}>
        You must be at least 18 years old to use the Service. By using the
        Service, you represent that you meet this requirement and have the legal
        authority to enter into these Terms.
      </p>

      <h2 className={headingStyle}>3. Account Registration</h2>
      <p className={sectionStyle}>
        You are responsible for maintaining the confidentiality of your account
        credentials and for all activity that occurs under your account. You
        agree to notify us immediately of any unauthorized use of your account.
      </p>

      <h2 className={headingStyle}>4. Subscriptions and Billing</h2>
      <div className={sectionStyle}>
        <ul className="list-disc list-inside space-y-2">
          <li>
            <strong>Free Tier:</strong>{" "}
            A free plan is available with limited features and usage. No payment
            information is required.
          </li>
          <li>
            <strong>Paid Plans:</strong>{" "}
            Paid subscriptions are billed on a monthly recurring basis. Prices
            are listed in USD on our{" "}
            <a
              href="/pricing"
              className="underline text-accent-light dark:text-accent"
            >
              Pricing page
            </a>.
          </li>
          <li>
            <strong>Auto-Renewal:</strong>{" "}
            Paid subscriptions automatically renew each billing cycle. Your
            payment method will be charged at the beginning of each billing
            period unless you cancel before the renewal date.
          </li>
          <li>
            <strong>Cancellation:</strong>{" "}
            You may cancel your subscription at any time from your Settings
            page. Cancellation takes effect at the end of the current billing
            period. You will retain access to paid features until then.
          </li>
          <li>
            <strong>Plan Changes:</strong>{" "}
            You may upgrade or downgrade your plan at any time. Upgrades take
            effect immediately; downgrades take effect at the start of the next
            billing cycle.
          </li>
          <li>
            <strong>Price Changes:</strong>{" "}
            We may adjust pricing with at least 30 days' notice. Continued use
            after the effective date constitutes acceptance of the new pricing.
          </li>
        </ul>
      </div>

      <h2 className={headingStyle}>5. Acceptable Use</h2>
      <div className={sectionStyle}>
        You agree not to:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            Use the Service for any unlawful, harmful, or abusive purposes.
          </li>
          <li>
            Create bots that distribute spam, malware, or misleading content.
          </li>
          <li>
            Attempt to gain unauthorized access to the Service or its systems.
          </li>
          <li>Interfere with or disrupt the integrity of the Service.</li>
          <li>
            Violate any applicable laws or third-party rights in connection with
            your use of the Service.
          </li>
        </ul>
      </div>

      <h2 className={headingStyle}>
        6. Prohibited Content: Child Sexual Abuse Material
      </h2>
      <div className={sectionStyle}>
        <p className="mb-3">
          We maintain a zero-tolerance policy toward child sexual abuse material
          (CSAM) and any content that sexually exploits or endangers minors. You
          may not use the Service to create, generate, store, distribute, or
          solicit:
        </p>
        <ul className="list-disc list-inside space-y-1 mb-3">
          <li>
            Sexual or sexually suggestive content involving minors (anyone under
            18), whether real or fictional.
          </li>
          <li>
            Content that grooms, lures, or facilitates the exploitation of
            minors.
          </li>
          <li>
            Bots designed to engage minors in sexual conversation or roleplay.
          </li>
        </ul>
        <p>
          Violation of this section will result in immediate and permanent
          account termination without notice or refund. We will report
          violations and preserve all associated data for law enforcement,
          including the National Center for Missing & Exploited Children (NCMEC)
          where applicable.
        </p>
      </div>

      <h2 className={headingStyle}>
        7. User Responsibility for Bot Content and Output
      </h2>
      <p className={sectionStyle}>
        You are solely responsible for the bots you create and all content they
        generate, transmit, or display to end users. prompt2bot provides the
        platform and tools but does not control, endorse, or assume liability
        for the output of your bots. You agree to ensure your bots comply with
        all applicable laws and these Terms, and you accept full responsibility
        for any claims, damages, or legal actions arising from your bots'
        behavior or output.
      </p>

      <h2 className={headingStyle}>8. AI-Generated Content Disclaimer</h2>
      <p className={sectionStyle}>
        Bots created on the Service are powered by third-party AI models. AI
        output may be inaccurate, incomplete, or misleading. prompt2bot does not
        guarantee the accuracy, reliability, or appropriateness of any
        AI-generated content. You should not rely on AI output as professional,
        legal, medical, or financial advice. You are responsible for reviewing
        and verifying all content your bots produce before it reaches end users.
      </p>

      <h2 className={headingStyle}>
        9. Autonomous Agents and Third-Party Content
      </h2>
      <p className={sectionStyle}>
        Bots created on the Service can autonomously browse, fetch, read, and
        act upon content from third-party websites, APIs, emails, images,
        documents, and other external sources that we do not control. Such
        content may contain inaccurate, malicious, or adversarial material,
        including hidden instructions designed to manipulate AI behavior
        ("prompt injection"). Although we employ safeguards, no safeguard is
        perfect, and a bot may be deceived or manipulated by third-party content
        into taking actions neither you nor we intended. You acknowledge this
        risk and agree that prompt2bot is not responsible or liable for actions
        your bots take, or for any resulting loss or damage, arising from
        third-party content your bots access or are exposed to.
      </p>

      <h2 className={headingStyle}>
        10. Financial Transactions and Credentials
      </h2>
      <p className={sectionStyle}>
        Bots on the Service may be configured to initiate payments, purchases,
        cryptocurrency or other blockchain transactions, or similar financial
        operations, including by using credentials, API keys, wallet addresses,
        or private keys that you choose to provide. You are solely responsible
        for (a) any credentials or secrets you entrust to your bots, (b) all
        transactions initiated by your bots, whether or not authorized or
        intended by you, and (c) limiting the funds and permissions accessible
        to your bots. Blockchain transactions are typically irreversible.
        prompt2bot is not a financial institution, money transmitter, broker,
        exchange, or custodian, does not provide financial advice, and cannot
        reverse, refund, or recover any transaction or funds. You assume all
        risk of loss arising from transactions initiated by your bots, including
        losses caused by errors, AI behavior, third-party content, or malicious
        actors.
      </p>

      <h2 className={headingStyle}>11. Third-Party Services</h2>
      <p className={sectionStyle}>
        The Service integrates with third-party platforms including but not
        limited to Telegram, WhatsApp, Facebook Messenger, and various AI model
        providers. Your use of these platforms is subject to their respective
        terms of service. We are not responsible for the availability,
        performance, or policies of any third-party service, and we make no
        warranties regarding their operation or continued compatibility with the
        Service.
      </p>

      <h2 className={headingStyle}>12. Intellectual Property</h2>
      <p className={sectionStyle}>
        All content, code, designs, and trademarks associated with the Service
        are the property of prompt2bot or its licensors and are protected by
        applicable intellectual property laws. You retain ownership of content
        you create through the Service, but grant us a license to host, display,
        and transmit it as necessary to operate the Service.
      </p>

      <h2 className={headingStyle}>13. Data and Privacy</h2>
      <p className={sectionStyle}>
        Our collection and use of personal data is described in our{" "}
        <a
          href={privacyPath}
          className="underline text-accent-light dark:text-accent"
        >
          Privacy Policy
        </a>
        . By using the Service, you consent to the practices described therein.
        Conversations and data processed through your bots are accessible to you
        (the bot owner) and to us for debugging and service improvement
        purposes.
      </p>

      <h2 className={headingStyle}>14. Indemnification</h2>
      <p className={sectionStyle}>
        You agree to indemnify, defend, and hold harmless prompt2bot, its
        officers, directors, employees, and agents from and against any claims,
        liabilities, damages, losses, and expenses (including reasonable
        attorneys' fees) arising out of or in connection with your use of the
        Service, your bots' content or behavior, your violation of these Terms,
        or your violation of any rights of a third party.
      </p>

      <h2 className={headingStyle}>15. Disclaimer of Warranties</h2>
      <p className={sectionStyle}>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
        ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
        IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
        UNINTERRUPTED, ERROR-FREE, OR SECURE.
      </p>

      <h2 className={headingStyle}>16. Limitation of Liability</h2>
      <p className={sectionStyle}>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL PROMPT2BOT BE
        LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
        DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, FUNDS, CRYPTOCURRENCY, OR
        DIGITAL ASSETS, WHETHER INCURRED DIRECTLY OR INDIRECTLY, ARISING FROM
        YOUR USE OF THE SERVICE OR FROM ACTIONS TAKEN BY YOUR BOTS. OUR TOTAL
        LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS
        PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
      </p>

      <h2 className={headingStyle}>17. Termination</h2>
      <p className={sectionStyle}>
        We may suspend or terminate your access to the Service at any time for
        violation of these Terms or for any other reason at our sole discretion.
        Upon termination, your right to use the Service ceases immediately. You
        may terminate your account at any time by contacting us.
      </p>

      <h2 className={headingStyle}>
        18. Cooperation with Law Enforcement
      </h2>
      <p className={sectionStyle}>
        We will cooperate with law enforcement agencies and comply with valid
        legal requests, court orders, and subpoenas. We reserve the right to
        disclose any information necessary to satisfy applicable laws,
        regulations, or governmental requests, and to report illegal activity to
        the appropriate authorities.
      </p>

      <h2 className={headingStyle}>19. Governing Law</h2>
      <p className={sectionStyle}>
        These Terms shall be governed by and construed in accordance with the
        laws of the State of New Mexico, United States, without regard to its
        conflict of law provisions. Any disputes arising under these Terms shall
        be resolved exclusively in the state or federal courts located in the
        State of New Mexico, and you consent to the personal jurisdiction of
        such courts.
      </p>

      <h2 className={headingStyle}>20. Severability</h2>
      <p className={sectionStyle}>
        If any provision of these Terms is held to be invalid, illegal, or
        unenforceable, the remaining provisions shall continue in full force and
        effect. The invalid provision shall be modified to the minimum extent
        necessary to make it valid and enforceable while preserving its original
        intent.
      </p>

      <h2 className={headingStyle}>21. Changes to These Terms</h2>
      <p className={sectionStyle}>
        We reserve the right to update these Terms at any time. We will notify
        you of material changes by posting the updated Terms on this page with a
        new "Last updated" date. Continued use of the Service after changes
        constitutes acceptance of the updated Terms.
      </p>

      <h2 className={headingStyle}>22. Contact</h2>
      <p className={sectionStyle}>
        If you have questions about these Terms, please contact us:
      </p>
      <div className="text-sm">
        <Support />
      </div>
    </div>
  );
};
